<template>
  <div>
    <Grid
      @api="getUsers()"
      :headers="headers"
      :payload="{results, count, next, previous}"
      @filter="showFilterModal = true"
      @clearFilters="$refs.desktopFilters.reset()"
    >
      <template #buttons>
        <base-button @click="$router.push('users/create')" size="sm" action="create_user">
          <base-icon icon="file-plus" class="text-lg" />
        </base-button>
      </template>

      <template #filter>
        <UserFilter ref="desktopFilters" />
      </template>

      <template #[`head.actions`]>
        <span class="bg-secondary"></span>
      </template>

      <template #[`body.email`]="{item}">
        <div class="flex flex-col">
          <div
            class="leading-5 font-medium truncate"
            v-toolip="{content: item.email, placement: 'top'}"
          >
            {{ item.email }}
          </div>
          <div class="leading-5 text-secondary-dark">
            {{ item.name }}
          </div>
        </div>
      </template>
      <template #[`body.profile`]="{item}">
        {{
          item.companies && item.companies.length > 0
            ? item.companies
                .reduce((accumulator, company) => {
                  return `${accumulator} ${company.company_name} ${
                    company.is_admin ? '- Admin' : '- Basic'
                  },`
                }, '')
                .slice(0, -1)
            : 'GWF Staff'
        }}
      </template>
      <template #[`body.services`]="{item}">
        <div
          v-if="item.sonico_profile"
          class="flex-shrink-0 my-1 inline-flex flex-col text-xs leading-5 font-semibold"
        >
          <span class="text-primary">Sonico: </span>
          <span class="">{{ item.sonico_profile.label }}</span>
        </div>
      </template>
      <template #[`body.is_active`]="{item}">
        <span
          :class="[
            'px-2 inline-flex leading-6 font-semibold rounded text-white',
            item.is_active ? 'bg-green-light' : 'bg-orange-light',
          ]"
        >
          {{
            item.is_active
              ? $t('mygwf.components.Grid.active')
              : $t('mygwf.components.Grid.inactive')
          }}
        </span>
      </template>
      <template #[`body.date_joined`]="{item}">
        <span>{{ item.date_joined ? $d(new Date(item.date_joined), 'long') : '-' }}</span>
      </template>
      <template #[`body.last_login`]="{item}">
        <span>{{ item.last_login ? $d(new Date(item.last_login), 'long') : '-' }}</span>
      </template>
      <template #[`body.actions`]="{item}">
        <div class="flex justify-end space-x-4">
          <base-button-icon
            v-if="$store.getters['auth/isStaff'] && !!item.companies && item.is_active"
            action="impersonate_user"
            :loading="pendingImpersonationOf === item[pk]"
            :disabled="!!pendingImpersonationOf"
            @click="onImpersonate(item)"
            icon="users"
            class="text-primary-light"
          />
          <base-button-icon
            v-if="!item.is_active"
            @click="sendActivation(item)"
            action="activate_user"
            class="text-orange-default"
            tooltip="activate_user"
            :loading="item[pk] === currentSending"
          />
          <base-button-icon @click="$store.dispatch('app/navigate', item)" action="edit_user" />
          <base-button-icon
            v-if="item[pk] !== $store.getters['auth/currentUserId']"
            @click="deleteUser(item)"
            action="delete_user"
            class="text-red-default"
            :loading="item[pk] === currentDeleting"
          />
        </div>
      </template>
    </Grid>

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <UserFilter :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>
  </div>
</template>

<script>
import UserFilter from './UserFilter'
import {BaseButton, BaseButtonIcon, BaseIcon, BaseModal} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'

export default {
  data() {
    return {
      list: {},
      pk: this.$store.getters['users/pk'],
      currentDeleting: 0,
      currentSending: 0,
      pendingImpersonationOf: '',
      headers: {
        email: {
          label: 'user',
          order: true,
          className: 'w-1/4',
        },
        profile: {
          label: 'profile',
          order: false,
        },
        services: {
          label: 'services',
          order: false,
        },
        is_active: {
          label: 'status',
          order: true,
        },
        date_joined: {
          label: 'created',
          order: true,
        },
        last_login: {
          label: 'last_login',
          order: true,
        },
        actions: {
          label: '',
          order: false,
        },
      },
      showFilterModal: false,
    }
  },
  methods: {
    async getUsers() {
      try {
        this.list = await this.$store.dispatch('users/get')
      } catch (error) {}
    },
    sendActivation(user) {
      this.currentSending = user[this.pk]
      this.$store
        .dispatch('users/resendActivationToken', user)
        .then(data => {
          this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `user_resend_activation`,
          })
        })
        .catch(() =>
          this.$store.dispatch('app/notification', {
            type: 'error',
            group: 'app',
            text: `user_resend_activation`,
          })
        )
        .finally(() => (this.currentSending = 0))
    },
    deleteUser(user) {
      if (
        confirm(
          this.$t(`mygwf.components.Confirmation.user_confirm_delete`, {
            email: user.email,
          })
        )
      ) {
        this.currentDeleting = user[this.pk]
        this.$store
          .dispatch('users/delete', user)
          .then(data => {
            this.$store.dispatch('app/notification', {
              type: 'success',
              group: 'app',
              text: `deleted`,
            })
            this.list.results = this.list.results.filter(res => res[this.pk] !== user[this.pk])
            this.list.count--
          })
          .catch(err => {
            this.$store.dispatch('app/notification', {
              type: 'error',
              group: 'app',
              text: `deleted`,
            })
          })
          .finally(() => (this.currentDeleting = 0))
      }
    },
    async onImpersonate(user) {
      this.pendingImpersonationOf = user[this.pk]
      await this.$store.dispatch('auth/impersonate', user[this.pk])
      this.pendingImpersonationOf = ''
      this.$router.push('Dashboard')
    },
  },
  computed: {
    count() {
      return this.list && this.list.count ? this.list.count : 0
    },
    next() {
      return this.list && this.list.next ? this.list.next : null
    },
    previous() {
      return this.list && this.list.previous ? this.list.previous : null
    },
    results() {
      return this.list && this.list.results ? this.list.results : []
    },
  },
  components: {
    UserFilter,
    BaseButton,
    BaseButtonIcon,
    Grid,
    BaseIcon,
    BaseModal,
  },
}
</script>
